import type { LeDataSessionSummary } from '~/types/leData';
import type { LensSpace } from '~/types/leLens';

import {
  GROWTH_DIRECT_CHANNEL,
  GROWTH_RAIL_CHANNELS,
  activeGrowthChannel,
  activeGrowthLifecycle,
} from '../LeLens/catalogue/growth/navigation';
import type { GrowthSourceId, GrowthViewState } from '../LeLens/catalogue/growth/types';

/** Main team plus two saved team pins; the current page remains visible even outside those slots. */
export function railTeams(teams: Array<LensSpace>, activeId: string | null, expanded: boolean): Array<LensSpace> {
  const base = expanded
    ? teams
    : [
        ...teams.filter((team) => team.main),
        ...teams.filter((team) => !team.main && Boolean(team.pinOrder)).slice(0, 2),
      ];
  const active = teams.find((team) => team.id === activeId);
  return active && !base.some((team) => team.id === active.id) ? [...base, active] : base;
}

/** The rail is a launch surface, not a complete archive. The full history is
 * still available in askRichard; this returns a scannable working set. */
export function compactRecentSessions(
  sessions: Array<LeDataSessionSummary>,
  activeSessionId: string | null,
  limit = 5,
): Array<LeDataSessionSummary> {
  const active = activeSessionId ? sessions.find((session) => session.sessionId === activeSessionId) : undefined;
  const visible = sessions.slice(0, limit);
  if (active && !visible.some((session) => session.sessionId === active.sessionId)) {
    // A title is not an identity: two investigations can start with the same
    // question and diverge. Keep the open session by id, never by a display
    // label that could hide it.
    if (visible.length < limit) {
      visible.push(active);
    } else {
      visible[visible.length - 1] = active;
    }
  }
  return visible;
}

/** Caps a frequently growing collection while preserving the currently open object. */
export function keepActiveAtLimit<T extends { id: string }>(
  items: Array<T>,
  activeId: string | null,
  limit: number,
): Array<T> {
  const visible = items.slice(0, limit);
  const active = activeId ? items.find((item) => item.id === activeId) : undefined;
  if (active && !visible.some((item) => item.id === active.id)) {
    // Same rule as compactRecentSessions: append while there is room, and only displace the
    // last row once the cap is actually reached.
    if (visible.length < limit) {
      visible.push(active);
    } else {
      visible[visible.length - 1] = active;
    }
  }
  return visible;
}

export type RailRowState = 'current' | 'open' | 'idle';

/**
 * A parent row whose child page is on screen reads as open, not current, so a screen reader hears
 * one current page. Commercial and Growth overviews are the parent's own page.
 */
export function areaRowState(
  areaId: string,
  areaOpen: boolean,
  commercialAnalysisId: string | undefined,
  growthRowId: string | null,
): RailRowState {
  if (!areaOpen) {
    return 'idle';
  }
  const childOpen =
    (areaId === 'commercial' && commercialAnalysisId !== undefined && commercialAnalysisId !== 'overview') ||
    (areaId === 'growth' && growthRowId !== null);
  return childOpen ? 'open' : 'current';
}

/** Accommodation is open while one of its models is the page, and current otherwise. */
export function accommodationRowState(onAccommodation: boolean, modelSelected: boolean): RailRowState {
  if (!onAccommodation) {
    return 'idle';
  }
  return modelSelected ? 'open' : 'current';
}

/** The Accommodation models stay open only while the Accommodation analysis is the page. */
export function accommodationModelsShown(commercialShown: boolean, commercialAnalysisId: string | undefined): boolean {
  return commercialShown && commercialAnalysisId === 'product-accommodation';
}

export interface GrowthRailRow {
  id: string;
  label: string;
  /** Set for a channel row, which opens that channel's page (Direct is a sessions preset, the rest are paid media). */
  channel?: string;
  /** Set for a page row. */
  page?: 'gha' | 'lifecycle';
  /** Set when a page row opens the page on a source other than its default. */
  source?: GrowthSourceId;
}

export const GROWTH_RAIL_ROWS: ReadonlyArray<GrowthRailRow> = [
  ...GROWTH_RAIL_CHANNELS.map((channel) => ({ id: channel, label: channel, channel })),
  { id: 'gha', label: 'Google Hotel Ads', page: 'gha' },
  { id: 'site-conversion', label: 'Site conversion', page: 'lifecycle', source: 'sessions' },
  { id: 'direct', label: 'Direct', channel: GROWTH_DIRECT_CHANNEL },
];

/** The Growth rail row the address shows, or null on the overview and on pages the rail has no row for. */
export function growthRailRowId(state: GrowthViewState): string | null {
  const channel = activeGrowthChannel(state);
  if (channel) {
    return channel;
  }
  const lifecycle = activeGrowthLifecycle(state);
  if (lifecycle) {
    // The Email page has no row of its own.
    return lifecycle === 'email' ? null : lifecycle;
  }
  return state.page === 'gha' ? 'gha' : null;
}
